import PropTypes from 'prop-types';
import * as React from 'react';
import { createComponent } from '../core/create-component.js';
import { ColorSwatchLabelPositions, ColorSwatchGradientTypes, useColorSwatchesContext } from './color-swatches.context.js';
import { className } from '../../../core/utilities/classnames.utils.js';
import { VisuallyHidden } from '../visually-hidden/visually-hidden.component.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const propTypes = {
    title: PropTypes.string,
    primaryColor: PropTypes.string,
    secondaryColor: PropTypes.string,
    backgroundImage: PropTypes.string,
    disabled: PropTypes.bool,
    accessibleText: PropTypes.string,
    labelPosition: PropTypes.oneOf(ColorSwatchLabelPositions),
    gradient: PropTypes.oneOf(ColorSwatchGradientTypes)
};
const propKeysToRemove = Object.keys(propTypes).filter((k)=>k !== 'title');
const ColorSwatch = createComponent({
    displayName: 'ColorSwatch',
    root: 'div',
    propTypes,
    propKeysToRemove,
    propConfig: {
        processProps (_param) {
            var { style ={} , primaryColor , secondaryColor , accessibleText , backgroundImage , title , disabled  } = _param, props = _object_without_properties(_param, [
                "style",
                "primaryColor",
                "secondaryColor",
                "accessibleText",
                "backgroundImage",
                "title",
                "disabled"
            ]);
            const ctx = useColorSwatchesContext();
            return _object_spread_props(_object_spread({
                labelPosition: ctx === null || ctx === void 0 ? void 0 : ctx.labelPosition
            }, props), {
                'aria-disabled': !!disabled,
                accessibleText: accessibleText || title,
                title: undefined,
                disabled: disabled,
                role: 'presentation',
                style: _object_spread_props(_object_spread({}, style), {
                    backgroundColor: primaryColor !== null ? primaryColor : undefined,
                    color: secondaryColor ? secondaryColor : undefined,
                    backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined
                })
            });
        },
        classNames (props) {
            const classNameSet = new Set([
                'swan-color-swatch'
            ]);
            const { disabled , gradient  } = props;
            if (disabled) classNameSet.add(`swan-color-swatch-disabled`);
            if (gradient) classNameSet.add(`swan-color-swatch-${gradient}-gradient`);
            return classNameSet;
        },
        wrapRoot ({ accessibleText , labelPosition  }, _dp, element) {
            if (accessibleText === null) return element;
            const labelClasses = className([
                'swan-color-swatch-accessible-label',
                labelPosition && labelPosition !== 'top' ? `swan-color-swatch-accessible-label-${labelPosition}` : undefined
            ]);
            return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("span", {
                className: "swan-color-swatch-wrapper"
            }, element, /*#__PURE__*/ React.createElement(VisuallyHidden, {
                className: labelClasses
            }, accessibleText)));
        }
    }
});
ColorSwatch.defaultProps = {
    primaryColor: 'transparent',
    disabled: false
};

export { ColorSwatch };
